import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X, Check } from 'lucide-react';
import api from '../api/client';
import { useDropdownPortal, useOutsideClose } from './FilterBar';

// Multi-select dropdown (shared with company + dept) — panel portalled to
// document.body so it always paints above the data grid (backdrop-filter on
// .glass cards otherwise traps z-index inside the filter bar's stacking context)
function MultiDropdown({ value = [], onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);
  const pos = useDropdownPortal(open, ref, 260);
  useOutsideClose(open, [ref, panelRef], () => setOpen(false));
  const toggle = v => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  const label = value.length === 0 ? placeholder : value.length === 1
    ? (options.find(o => o.value === value[0])?.label || value[0])
    : `${value.length} selected`;
  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 150 }}>
      <button type="button" onClick={() => setOpen(o => !o)} className="input-dark text-xs py-1"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, cursor: 'pointer', width: '100%', textAlign: 'left' }}>
        <span style={{ color: value.length ? '#e2e8f0' : '#475569', fontSize: 12 }}>{label}</span>
        <ChevronDown size={11} style={{ color: '#475569', flexShrink: 0 }}/>
      </button>
      {open && pos && createPortal(
        <div ref={panelRef} style={{ position: 'fixed', top: pos.top, left: pos.left, right: pos.right, zIndex: 99999, minWidth: pos.minWidth, width: 'max-content', maxWidth: 260, background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '4px 0', boxShadow: '0 8px 24px rgba(0,0,0,0.5)', maxHeight: 220, overflowY: 'auto' }}>
          {options.map(o => {
            const sel = value.includes(o.value);
            return (
              <button key={o.value} type="button" onClick={() => toggle(o.value)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 12px', background: sel ? 'rgba(99,102,241,0.15)' : 'transparent', border: 'none', cursor: 'pointer', color: sel ? '#818cf8' : '#94a3b8', fontSize: 12, textAlign: 'left', whiteSpace: 'nowrap' }}>
                <span style={{ width: 14, height: 14, border: `1px solid ${sel ? '#818cf8' : '#334155'}`, borderRadius: 3, background: sel ? 'rgba(99,102,241,0.3)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {sel && <Check size={9} style={{ color: '#818cf8' }}/>}
                </span>
                {o.label}
              </button>
            );
          })}
          {value.length > 0 && (
            <button type="button" onClick={() => onChange([])}
              style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 12px', background: 'transparent', border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', color: '#ef4444', fontSize: 11, marginTop: 2 }}>
              <X size={10}/> Clear
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

// companyIds: string[] of company IDs, deptId: string (single — dept filtered per company context)
export default function CompanyDeptFilter({ companyId, deptId, onChange, style }) {
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Support both legacy single-string and new array format
  const companyIds = Array.isArray(companyId)
    ? companyId
    : (companyId ? [companyId] : []);

  useEffect(() => {
    api.get('/employees/meta/companies').then(r => setCompanies(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (companyIds.length === 1) {
      api.get('/employees/meta/departments', { params: { company_id: companyIds[0] } })
        .then(r => setDepartments(r.data || [])).catch(() => {});
    } else {
      setDepartments([]);
      if (deptId) onChange(companyIds, null);
    }
  }, [JSON.stringify(companyIds)]); // eslint-disable-line

  const coOptions = companies.map(c => ({ value: c.id, label: `${c.name} (${c.code})` }));
  const deptOptions = departments.map(d => ({ value: d.id, label: d.name }));

  return (
    <div className="flex items-center gap-2 flex-wrap" style={style}>
      <MultiDropdown
        value={companyIds}
        onChange={ids => onChange(ids, null)}
        options={coOptions}
        placeholder="All Companies"
      />
      {companyIds.length === 1 && departments.length > 0 && (
        <select
          value={deptId || ''}
          onChange={e => onChange(companyIds, e.target.value || null)}
          className="input-dark text-xs py-1"
          style={{ minWidth: 160 }}
        >
          <option value="">All Departments</option>
          {deptOptions.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
      )}
    </div>
  );
}
